// OSB MS API utility functions -- for OSB MS data calls

import config from "../config";
import { getHttpClient } from "./api";
import {
  getGlobalValue,
  getAuthToken,
  isFrench,
  setError,
  filterAvailableProducts,
  productRequested,
  setGlobalValue,
  setGlobal,
} from "./global-util";
import { dropSession } from "./session";
import {
  listSize,
  getProp,
  isObject,
  isEmpty,
  getTreeProp,
  jsonParse,
  isEmptyObject,
  isNumber,
  makeShortDateStamp,
} from "./util";
import {
  extractComparables,
  extractMsResult,
  extractRentalComparables,
  extractReportUrl,
  transformEnvironmental,
} from "./oss-ms-transfom";
import getLogger from "./debug-logger";
import { setAndStoreGlobalValue, setAndStoreGlobals, storeRequestedProducts } from "./stored-data";
import { isCondo } from "./features-config";
import { getPolMarker, translateFeatKey } from "./features-sources";
import { buildMsRequestAddress, buildOssMsRequest, buildUserUpdates } from "./oss-ms-util";
import { RENTAL } from "./globals";

const log = getLogger("oss-ms-api", 1);

const { ossMsApiEndpoint, ossApiTimeout } = config;

//----------------------------------------------------------
// send OSB POST request
//----------------------------------------------------------
const osbPost = async (postData) => {
  let errorMsg = "";
  const conf = {
    method: "POST",
    url: ossMsApiEndpoint,
    validateStatus: null, // resolve error responses
    data: postData,
  };
  const token = getAuthToken();
  if (!token) {
    log("Access token not available"); //--------------- log
    return null;
  }
  conf.headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const client = getHttpClient(ossApiTimeout);
  log(`Sending POST request to`, conf.url, "REQUEST DATA >>>", conf.data, "<<< END"); //-------- log
  let res = null;
  try {
    res = await client.request(conf);
    const data = getProp(res, "data");
    // handle access denied
    if (getTreeProp(data, "error") === "invalid_token") {
      log("Access denied"); //----------------------- log
      setError("Access denied");
      dropSession();
      return data;
    }
    // strip debug info from response data
    if (!!getTreeProp(data, "Results", 0, "Result", "Debug")) {
      log(`stripping debug info from response`); //----------------------- log
      delete data.Results[0].Result.Debug;
    }
    log("Microservice response received", "RESPONSE DATA >>>", data, "<<< END"); //---------- log
    return data;
  } catch (x) {
    const msg = (typeof x === "object" && x.message) || x + "";
    log(msg); //----------------- log
  }
  return null;
};

// Property Stats JSON translator
const translateStatsJson = (statsJson) => {
  const stats = jsonParse(statsJson);
  // translate some keys
  ["PropertyOccupancy", "Tenure", "ZoningDescription"].forEach((k) => {
    let kk = k + "TypeCode";
    stats[kk] = stats[k];
    delete stats[k];
  });
  return stats;
};

//----------------------------------------------------------
// load Comparables only
//----------------------------------------------------------
export const loadComparables = async () => {
  const isRentalMode = getGlobalValue("COMPARABLES_MODE") === RENTAL;
  const radius = Number(getGlobalValue("COMPARABLES_RADIUS"));
  const history = Number(getGlobalValue("COMPARABLES_HISTORY"));
  const maxNum = Number(getGlobalValue("COMPARABLES_NUMBER"));
  if ((!radius && radius !== 0) || !history || !maxNum) {
    log(`missing required configuration values`); //------------------ log
    return null;
  }
  setGlobal({ COMPARABLES_ERROR: null });
  setAndStoreGlobalValue("REPORT_URL", null);
  const customAttr = {
    ComparablesSearchHistoryDays: history === 360 ? 365 : history, // days,
    NumberOfComparables: maxNum,
    ComparablesRadiusMetres: radius && isNumber(radius) ? radius : undefined,
  };
  let updates = buildUserUpdates() || {};
  // build Property object updates
  let prop = getProp(updates, "Property") || {};
  prop.HistoricalDate = makeShortDateStamp(); // today's date as 'YYYY-MM-DD' (e.g. '2024-07-25')
  // provide Rental Type if rental mode or RentalValuation product requested for Report
  if (isRentalMode || productRequested("RentalValuation")) {
    prop = { ...prop, RentalUnitTypeCode: getGlobalValue("RENTAL_TYPE") };
  }
  if (!isEmpty(prop)) updates.Property = prop;
  const prodList = isRentalMode ? ["RentalComparables"] : ["MarketValueComparables"];

  storeRequestedProducts(prodList); // update the list of requested products

  const reqObj = buildOssMsRequest(prodList, customAttr, updates);

  const data = await osbPost(reqObj);

  if (!data) {
    setGlobalValue("COMPARABLES_ERROR", "COMPARABLES_ERROR");
    return null;
  }
  const prodData = extractMsResult(data);

  const comps = isRentalMode ? extractRentalComparables(prodData) : extractComparables(prodData);
  const size = listSize(comps);

  log(`${size} comparables returned`, comps); //----------- log

  // set or update global comparables list
  setGlobal({
    COMPARABLES: comps,
    COMPARABLES_ERROR: listSize(comps) ? null : "COMPARABLES_ERROR",
  });

  return comps;
};

//----------------------------------------------------------
// fetch products via OSB call
//----------------------------------------------------------
export const fetchMsProducts = async (productArray, attrObj = null, propObj = null) => {
  const isRentalMode = getGlobalValue("COMPARABLES_MODE") === RENTAL;
  setGlobal({
    FLOODSCORE_ERROR: null,
    WILDFIRE_ERROR: null,
    EARTHQUAKE_ERROR: null,
    REPORT_ERROR: null,
  });

  setAndStoreGlobalValue("OSB_PRODUCTS_REQUESTED", true);

  // requesting PDF report requires including all previously called products
  const hasReport = productArray.includes("iClarifyFinancialResidentialReport");
  hasReport && setGlobalValue("REPORT_URL", null);

  const compOaks = getGlobalValue("COMPARABLES_OAKS"); // selected comparables - indicates if Comparables product should be included
  const storedProducts = getGlobalValue("REQUESTED_PRODUCTS") || [];
  const comparablesProducts = ["RentalComparables", "MarketValueComparables"];
  const allProducts = hasReport
    ? [...productArray, ...storedProducts].filter(
        (product) => compOaks || !comparablesProducts.includes(product)
      )
    : productArray;

  storeRequestedProducts(productArray);

  let updates = buildUserUpdates() || {};
  let prop = getProp(updates, "Property") || {};

  if (isObject(propObj)) {
    Object.assign(prop, propObj); // merge Property objects
    log(`merging additional Property data`, propObj); //----------- log
  }

  prop.HistoricalDate = makeShortDateStamp(); // for HPIHistorical

  if (isRentalMode || productRequested("RentalComparables")) {
    prop = { ...prop, RentalUnitTypeCode: getGlobalValue("RENTAL_TYPE") };
  }

  const updStatsJson = getGlobalValue("STATS_UPDATED");
  if (updStatsJson) {
    prop.PropertyStats = translateStatsJson(updStatsJson);
  }

  if (!isEmpty(prop)) {
    updates.Property = prop;
  }
  // Redundant requirement:retrieve comparables configuration values, for comparables only
  const radius = Number(getGlobalValue("COMPARABLES_RADIUS"));
  const history = Number(getGlobalValue("COMPARABLES_HISTORY"));
  const maxNum = Number(getGlobalValue("COMPARABLES_NUMBER"));

  const customAttr = compOaks
    ? {
        ...(attrObj || {}),
        ComparablesSearchHistoryDays: history === 360 ? 365 : history, // days,
        ComparablesProperties: compOaks,
        NumberOfComparables: maxNum,
        ComparablesRadiusMetres: radius && isNumber(radius) ? radius : undefined,
      }
    : attrObj;

  const reqObj = buildOssMsRequest(allProducts, customAttr, updates);

  if (!reqObj) {
    log(`request object cannot be produced`); //------------------- log
    return null;
  }
  const data = await osbPost(reqObj);
  if (!data) {
    return null;
  }
  return extractMsResult(data);
};

//----------------------------------------------------------
// fetch Report URL, for Comparables view
//----------------------------------------------------------
export const updateReportUrlOsb = async () => {
  const result = await fetchMsProducts(["iClarifyFinancialResidentialReport"]);
  const reportUrl = extractReportUrl(result);
  log(`report URL from Microservice: '${reportUrl}'`); //-------------- log

  setAndStoreGlobalValue("REPORT_URL", reportUrl || "");
  return reportUrl;
};
//----------------------------------------------------------
// fetch Environmental products
// including Wildfire Score (not available via OSS)
//----------------------------------------------------------
export const loadEnvProducts = async (floodMapConf = null) => {
  // providing incomplete floodMapConf here (missing boundaries) causes problem with BE
  let mapConf = jsonParse(floodMapConf || getGlobalValue("FLOOD_MAP_CONFIG_JSON"));

  setGlobal({
    FLOODSCORE_ERROR: null,
    WILDFIRE_ERROR: null,
    EARTHQUAKE_ERROR: null,
    ACTIVE_WILDFIRES_ERROR: null,
    REPORT_ERROR: null,
    REPORT_URL: null,
  });
  const products = [
    "WildfireIndex",
    "ActiveWildfires",
    "Earthquake",
    "iClarifyFinancialResidentialReport",
    "FloodScore",
  ].filter(Boolean);

  const reqProps = isObject(mapConf) ? { FloodMap: mapConf } : undefined;

  let res = await fetchMsProducts(products, null, reqProps);

  if (!res) {
    log(`no Environmental data returned`, res); //--------------- log
    setGlobal({
      FLOODSCORE_ERROR: "FLOODSCORE_ERROR",
      WILDFIRE_ERROR: "WILDFIRE_ERROR",
      EARTHQUAKE_ERROR: "EARTHQUAKE_ERROR",
    });
    return;
  }
  const updates = transformEnvironmental(res);
  if (!isEmptyObject(updates)) {
    log(`updating and caching globals`, updates); //----------- log
    setAndStoreGlobals(updates);
  }
};

//----------------------------------------------------------
// fetch sources for property features
//----------------------------------------------------------
export const loadFeatSources = async () => {
  const propType = getGlobalValue("PROPERTY_TYPE");
  if (!propType) {
    log(`no property type, features sources API call dropped`); //------------------ log
  }

  const condo = isCondo(propType);

  // validate and transform requested product name (1 = microservice call)
  const osbProdNames = filterAvailableProducts(["iClarifyFinancialResidentialPrefill"], 1);
  const reqObj = {
    microservice: "opta-service-bus",
    headers: {
      "Accept-Language": isFrench() ? "fr" : "en",
    },
    params: {
      Products: osbProdNames.join(),
    },
    body: {
      ProductRequest: [
        {
          Address: buildMsRequestAddress(),
        },
      ],
    },
  };

  const data = await osbPost(reqObj);
  if (!data) {
    return null;
  }

  const dataKey = condo ? "FeaturesSources" : "FeatureSources";

  const sources = getTreeProp(data, dataKey);

  if (!listSize(sources)) {
    log(`could not retrieve features sources`); //------------------ log
    return null;
  }

  const featKey = condo ? "CondoConstructionFeatureCode" : "ResidentialConstructionFeatureCode";
  const sourceKey = condo
    ? "CondoConstructionFeatureSourceCode"
    : "ResidentialConstructionFeatureSourceCode";
  const sourceMapObj = {};

  sources.forEach((it) => {
    let featCode = getProp(it, featKey);
    if (!featCode) return null;
    const srcCode = getProp(it, sourceKey);
    if (!srcCode) return null;
    // translate OSB to OSS key
    featCode = translateFeatKey(featCode, condo);
    sourceMapObj[featCode] = getPolMarker(srcCode, condo);
  });
  log(`features sources mapping`, sourceMapObj); //----------- log
  setAndStoreGlobalValue("FEATURES_SOURCES", sourceMapObj);
  return sourceMapObj;
};
